import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, History, User2 } from "lucide-react";

const ACCION_LABEL = {
  alta: "Alta de reserva",
  alta_revision: "Alta (a revisar)",
  edicion: "Edición de datos",
  cambio_estado: "Cambio de estado",
  cambio_modalidad: "Cambio de modalidad",
  ajuste_saldo: "Ajuste de saldo",
  merge_source: "Vinculación de canal",
  actualizacion_bokun: "Actualización desde Bokun",
  confirmacion_bandeja: "Confirmada desde bandeja",
  unificacion_horario: "Unificación de horario",
  correo_cambio_detalle: "Correo de cambio",
  correo_mensaje_cliente: "Mensaje de cliente",
  importacion_historica: "Importación histórica",
};

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return ts;
  }
}

function resumen(obj) {
  if (!obj || typeof obj !== "object") return null;
  const partes = Object.entries(obj)
    .filter(([k]) => !["created_at", "updated_at", "id", "sources"].includes(k))
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`);
  return partes.join(" · ");
}

export default function HistorialReservaDialog({ open, onOpenChange, reservaId }) {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    if (open && reservaId) {
      setLogs(null);
      api
        .get("/audit", { params: { entidad: "reserva", entidad_id: reservaId, limit: 100 } })
        .then(({ data }) => setLogs(data))
        .catch(() => setLogs([]));
    }
  }, [open, reservaId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="historial-reserva-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <History className="h-5 w-5" /> Historial de cambios
          </DialogTitle>
          <DialogDescription>Qué se cambió, quién y cuándo (registro de auditoría).</DialogDescription>
        </DialogHeader>
        {logs === null ? (
          <div className="flex items-center justify-center h-28">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Sin movimientos registrados.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-2 space-y-4">
            {logs.map((l) => (
              <li key={l.id} className="ml-4" data-testid={`historial-item-${l.id}`}>
                <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-slate-900 border-2 border-white" />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    {ACCION_LABEL[l.accion] || l.accion}
                  </span>
                  <span className="text-[11px] text-slate-400 whitespace-nowrap">{fmt(l.timestamp)}</span>
                </div>
                <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                  <User2 className="h-3 w-3" /> {l.usuario_nombre || "Sistema"}
                  <span className="text-slate-300">·</span>
                  <span className="capitalize">{l.origen}</span>
                </p>
                {resumen(l.despues) && (
                  <p className="text-[11px] text-slate-600 bg-slate-50 rounded px-2 py-1 mt-1 break-words">
                    {resumen(l.despues)}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
